import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { LocationEpisodeLinkSet } from 'models/flexible/LocationEpisodeLinkSet';
import type { AdminLinkSetShowFields } from 'services/api/fields';
import { Body } from 'styles/typography';

type UnlinkProps = {
  variant?: 'unlink';
  record: LocationEpisodeLinkSet<AdminLinkSetShowFields> | null;
  onCancel: () => void;
  onConfirm: () => void;
};

type RemoveProps = {
  variant: 'remove';
  record: LocationEpisodeLink | null;
  onCancel: () => void;
  onConfirm: () => void;
};

type Props = UnlinkProps | RemoveProps;

const VARIANT_CONFIG = {
  unlink: {
    title: 'Unlink Records',
    body: 'This will remove the link between these records. Would you like to continue?',
    buttonText: 'Unlink Records',
  },
  remove: {
    title: 'Remove Record',
    body: 'This will remove the record from the current link set. You can add it back later if needed.',
    buttonText: 'Remove Record',
  },
};

export default function UnlinkConfirmationModal({ variant = 'unlink', record, onCancel, onConfirm }: Props) {
  if (!record) return null;

  const config = VARIANT_CONFIG[variant];
  // Both record types expose `patientName` directly — no per-variant accessor needed.
  const patientName = record.patientName;

  return (
    <StandardModal title={config.title} colorTheme={ColorThemeName.Danger} onCancel={onCancel}>
      <ModalContent>
        {patientName && <PatientName>Patient: {patientName}</PatientName>}
        <Body>{config.body}</Body>
      </ModalContent>
      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='danger' onClick={onConfirm}>
          {config.buttonText}
        </Button>
      </Actions>
    </StandardModal>
  );
}

const PatientName = styled(Body)`
  display: block;
  font-weight: 500;
  margin: 8px 0 27px;
  text-align: center;
`;
